import React, { useState, useMemo } from 'react';
import { Badge, Breadcrumbs, Button, ChevronRight, EmptyState, SearchLg, Table, TextInput } from '@opendate-app/setlist';
import CrmPageLayout from '../../shared/components/CrmPageLayout';

const SORT_VALUES = {
  name: (obj) => obj.name.toLowerCase(),
  records: (obj) => obj.records_count || 0,
};

function CrmObjectsPage({ team, objects = [] }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState({ key: 'name', direction: 'asc' });

  const sortedAndFilteredObjects = useMemo(() => {
    const value = SORT_VALUES[sort.key];
    const dir = sort.direction === 'asc' ? 1 : -1;
    return objects
      .filter((obj) => obj.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => (value(a) < value(b) ? -dir : value(a) > value(b) ? dir : 0));
  }, [objects, searchTerm, sort]);

  const teamId = team && team.id;

  const getAttributesUrl = (attachableType) => {
    if (!teamId || !attachableType) return null;
    if (attachableType === 'Order') return `/teams/${teamId}/order-attributes`;
    return `/teams/${teamId}/crm-attributes/${encodeURIComponent(attachableType)}`;
  };

  const columns = [
    {
      key: 'name',
      header: 'Object',
      primary: true,
      sortable: true,
      render: (object) => (
        <div className="d-flex align-items-center" style={{ gap: 10 }}>
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle"
            style={{ width: 32, height: 32, minWidth: 32, background: 'var(--sl-bg-secondary)', color: 'var(--sl-text-tertiary)' }}
          >
            <i className={object.icon} aria-hidden="true" />
          </span>
          <Button as="a" href={getAttributesUrl(object.attachable_type)} variant="link-color">
            {object.name}
          </Button>
        </div>
      ),
    },
    { key: 'type', header: 'Type', width: '15%', render: () => <Badge size="sm" color="gray">Standard</Badge> },
    {
      key: 'records',
      header: 'Records',
      width: '15%',
      sortable: true,
      render: (object) => (object.records_count ? object.records_count.toLocaleString() : 'No records'),
    },
    {
      key: 'attributes',
      header: 'Attributes',
      width: '15%',
      render: (object) => (object.attributes_count ? object.attributes_count.toLocaleString() : '—'),
    },
    {
      key: 'actions',
      header: <span className="sr-only">Open</span>,
      align: 'right',
      width: '1%',
      render: (object) => (
        <a href={getAttributesUrl(object.attachable_type)} aria-label={`Open ${object.name} attributes`} style={{ color: 'var(--sl-text-tertiary)' }}>
          <ChevronRight width={20} height={20} aria-hidden="true" />
        </a>
      ),
    },
  ];

  return (
    <CrmPageLayout
      team={team}
      activeSidebarKey="settings"
      mainTopMargin={32}
      breadcrumb={<Breadcrumbs items={[{ label: 'Settings', href: teamId ? `/teams/${teamId}/crm-settings` : undefined }, { label: 'Objects' }]} />}
      title="Objects"
      subtitle="Modify and add objects in your CRM."
    >
      <div className="card">
        <div className="card-body p-0">
          <div className="border-bottom px-4 py-3">
            <div style={{ maxWidth: 360 }}>
              <TextInput
                leadingIcon={SearchLg}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search objects"
                aria-label="Search objects"
              />
            </div>
          </div>

          <Table
            columns={columns}
            data={sortedAndFilteredObjects}
            getRowKey={(object) => object.attachable_type}
            sort={sort}
            onSortChange={(key, direction) => setSort({ key, direction })}
            empty={<EmptyState size="sm" icon={SearchLg} title="No objects found." />}
          />
        </div>
      </div>
    </CrmPageLayout>
  );
}

export default CrmObjectsPage;
